import Grid from "@mui/material/Grid2";
import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import ReferenceRateUnitSelect from "view/calculationmethods/components/select/ReferenceRateUnitSelect";
import ReferenceRateBaseSelect from "view/calculationmethods/components/select/ReferenceRateBaseSelect";
import ReferenceRateDateTypeSelect from "view/calculationmethods/components/select/ReferenceRateDateTypeSelect";
import PJKRouteSelect from "components/selects/PJKRouteSelect";
import {
	LocationType,
	PayableOver,
	ReferenceAdjustmentType,
	ReferenceRateBase,
	ReferenceRateUnit,
	PjkTariff,
	ReferenceRateDateType,
} from "__generated__/graphql";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import PjkTariffSelect from "view/calculationmethods/components/select/PjkTariffSelect";
import ReferenceRateAdjustmentTypeSelect
	from "view/calculationmethods/components/select/ReferenceRateAdjustmentTypeSelect";
import PayableOverSelect from "view/calculationmethods/components/select/PayableOverSelect";
import { Fragment, ReactNode } from "react";
import FormField from "components/cck/FormField";
import NumberInput from "components/cck/NumberInput";
import type { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";

type FormValues = {
	agreements: {
		calculationMethod: {
			referenceRateUnit?: ReferenceRateUnit;
			referenceRateBase?: ReferenceRateBase;
			adjustmentAmount?: number;
			adjustmentType?: ReferenceAdjustmentType;
			route?: string;
			loadLocation?: {
				id: number;
				description: string;
				type: LocationType;
				locationGroups: {
					id: number;
					description: string
					type: LocationType;
				}[];
			};
			dischargeLocation?: {
				id: number;
				description: string;
				type: LocationType;
				locationGroups: {
					id: number;
					description: string
					type: LocationType;
				}[];
			};
			pjkTariff?: PjkTariff;
			adjustmentPrice?: number;
			payableOver?: PayableOver;
			preAgreed?: number;
			referenceRateDateType?: ReferenceRateDateType;
			minimumPrice?: number;
			minimumQuantityPayable?: number;
		}
	}[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

const ReferenceRateCalculationMethodForm = ({  index, currency, hiddenFields }: CalculationMethodFormByTypeProps) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();

	const referenceRateUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.referenceRateUnit` });
	const referenceRateBase = useWatch({ control, name: `agreements.${index}.calculationMethod.referenceRateBase` });
	const adjustmentType = useWatch({ control, name: `agreements.${index}.calculationMethod.adjustmentType` });
	const payableOver = useWatch({ control, name: `agreements.${index}.calculationMethod.payableOver` });

	const ReferenceRateAdjustmentPriceFormFields: FormField[] = [
		{
			id: "calculationMethod.adjustmentAmount",
			render: () => adjustmentType && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.adjustmentAmount`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_ADJUSTMENT_AMOUNT" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.adjustmentAmount`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={adjustmentType === ReferenceAdjustmentType.Flatprice && currency.symbol}
										addonAfter={adjustmentType === ReferenceAdjustmentType.Percentage && "%"}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.adjustmentType",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.adjustmentType`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_ADJUSTMENT_TYPE" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.adjustmentType`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<ReferenceRateAdjustmentTypeSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	const ReferenceRateCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.referenceRateUnit",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.referenceRateUnit`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_UNIT" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.referenceRateUnit`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<ReferenceRateUnitSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.referenceRateBase",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.referenceRateBase`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_BASE" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.referenceRateBase`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<ReferenceRateBaseSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.route",
			render: () => referenceRateBase === ReferenceRateBase.Route && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.route`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_BASE_ROUTE" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.route`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => <PJKRouteSelect {...field} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.loadLocation",
			render: () => referenceRateBase === ReferenceRateBase.LoadLocation && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.loadLocation`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_BASE_LOAD_LOCATION" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.loadLocation`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<LocationSelect
										{...field}
										value={field.value ?? null}
										onChange={option => field.onChange(option ?? undefined)}
										types={[ LocationType.Terminal, LocationType.Port ]} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.dischargeLocation",
			render: () => referenceRateBase === ReferenceRateBase.DischargeLocation && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.dischargeLocation`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_BASE_DISCHARGE_LOCATION" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.dischargeLocation`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<LocationSelect
										{...field}
										value={field.value ?? null}
										onChange={option => field.onChange(option ?? undefined)}
										types={[ LocationType.Terminal, LocationType.Port ]} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.pjkTariff",
			render: () => referenceRateUnit === ReferenceRateUnit.Pjk && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.pjkTariff`}>
						<FormGroup label={intl.formatMessage({ id: "PJK_TARIFF" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.pjkTariff`}
								control={control}
								render={({ field }) => (
									<PjkTariffSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.adjustmentPrice",
			render: () => (
				<Grid container size={12} spacing={2}>
					{ReferenceRateAdjustmentPriceFormFields
						.map(x => x.render())}
				</Grid>
			),
		},
		{
			id: "calculationMethod.payableOver",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.payableOver`}>
						<FormGroup label={intl.formatMessage({ id: "PAYABLE_OVER" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.payableOver`}
								control={control}
								render={({ field }) => (
									<PayableOverSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.preAgreed",
			render: () => payableOver === PayableOver.Preagreed && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.preAgreed`}>
						<FormGroup label={intl.formatMessage({ id: "ORDER_QUANTITY" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.preAgreed`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.referenceRateDateType",
			render: () => referenceRateUnit && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.referenceRateDateType`}>
						<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_DATE_TYPE" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.referenceRateDateType`}
								control={control}
								render={({ field }) => (
									<ReferenceRateDateTypeSelect referenceRateUnit={referenceRateUnit} {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumPrice",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.minimumPrice`}>
						<FormGroup label={intl.formatMessage({ id: "MINIMUM_PRICE" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.minimumPrice`}
								control={control}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumQuantityPayable",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.minimumQuantityPayable`}>
						<FormGroup label={intl.formatMessage({ id: "MINIMUM_QUANTITY_PAYABLE" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.minimumQuantityPayable`}
								control={control}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonAfter={intl.formatMessage({ id: "UNIT_MTON" })} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	return (
		<>
			{ReferenceRateCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default ReferenceRateCalculationMethodForm;
